import { FC, useEffect, useMemo } from 'react';

import classNames from 'classnames';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { AwardingBodyIdentifiers, formatUTCDate } from 'core';
import { useLazyGetTimelineQuery } from 'store/exams-search/examsSearchAPI';
import { useAppSelector } from 'store';
import { selectSystemDateTime } from 'store/features/featuresSlice';
import { GetQualificationLevelGroupIdForQualificationLevelId } from 'core/const/QualificationLevelGroupIdentifiers';

import { PageLoader } from '@britishcouncil/react-solas-ors3';

export const Timeline: FC<{ sessionId: number; awardingBodyId: number; qualificationLevelId: number }> = ({
  sessionId,
  awardingBodyId,
  qualificationLevelId,
}) => {
  const [getTimeline, { data: timelineData, isFetching }] = useLazyGetTimelineQuery();
  const systemDateTime = useAppSelector(selectSystemDateTime);

  useEffect(() => {
    getTimeline(sessionId);
  }, [getTimeline, sessionId]);

  const steps = useMemo(() => {
    if (!timelineData?.itemsQlg) return [];

    let qlgIndex = 0;
    if (awardingBodyId === AwardingBodyIdentifiers.Edexcel) {
      if (qualificationLevelId === 0) return [];

      let qualificationGroupId = GetQualificationLevelGroupIdForQualificationLevelId(
        awardingBodyId,
        qualificationLevelId
      );
      let timelineItem = timelineData.itemsQlg.find((x) => x.qualificationLevelGroupId === qualificationGroupId);
      qlgIndex = timelineData.itemsQlg.indexOf(timelineItem!);
    }

    return (
      timelineData.itemsQlg[qlgIndex]?.itemList.map((x, i) => {
        const nextItem = timelineData?.itemsQlg[qlgIndex]?.itemList[i + 1];
        let percentage = 0;

        if (nextItem) {
          const itemDateTime = DateTime.fromISO(x.date).toJSDate().getTime();
          const nextItemDateTime = DateTime.fromISO(nextItem.date).toJSDate().getTime();
          const itemToNowDiffTime = systemDateTime - itemDateTime;

          percentage =
            (1 - (nextItemDateTime - itemDateTime - itemToNowDiffTime) / (nextItemDateTime - itemDateTime)) * 100;
        }

        return {
          ...x,
          percentage: percentage < 0 ? 0 : percentage > 100 ? 100 : percentage,
        };
      }) ?? []
    );
  }, [timelineData, awardingBodyId, qualificationLevelId, systemDateTime]);

  const timelineHeader = useMemo(() => {
    if (!timelineData?.itemsQlg) return [];

    if (awardingBodyId === AwardingBodyIdentifiers.Edexcel) {
      if (qualificationLevelId === 0) return '';

      let qualificationGroupId = GetQualificationLevelGroupIdForQualificationLevelId(
        awardingBodyId,
        qualificationLevelId
      );

      let qualificationLevelGroupName = timelineData.itemsQlg.find(
        (x) => x.qualificationLevelGroupId === qualificationGroupId
      )?.qualificationLevelGroupName;

      return `${timelineData.title} - ${qualificationLevelGroupName} - ${timelineData.sessionName} (GMT/BST)`;
    }

    return `${timelineData.title} - ${timelineData.sessionName} (GMT/BST)`;
  }, [timelineData, awardingBodyId, qualificationLevelId]);

  if (isFetching) return <PageLoader />;

  if (!timelineData) return null;

  return (
    <div className="timeline">
      <TimelineTitle>{timelineHeader}</TimelineTitle>
      <TimelineList stepWidth={100 / steps.length}>
        {steps.map((item, i) => (
          <TimelineStep key={i} width={100 / steps.length} className={classNames({ done: item.isActive })}>
            {i < steps.length - 1 && <TimelineStepProgress stepPercentage={item.percentage}></TimelineStepProgress>}
            <TimelineStepDate>{formatUTCDate(item.date)}</TimelineStepDate>
            <TimelineStepMarker />
            <TimelineStepTitle>{item.event}</TimelineStepTitle>
          </TimelineStep>
        ))}
      </TimelineList>
    </div>
  );
};

const TimelineTitle = styled.p`
  text-align: center;
  margin-bottom: 0%;
  @media (min-width: 568px) {
    margin-bottom: 40px;
  }
`;

const TimelineList = styled.ol<{ stepWidth: number }>`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  position: relative;
  flex-direction: column;

  &::before {
    content: '';
    background: #99b9d2;
    width: 2px;
    height: calc(100% - 100px);
    position: absolute;
    left: calc(50% - 1px);
    top: 50px;
  }

  @media (min-width: 568px) {
    margin: 20px 0;
    flex-direction: row;

    &::before {
      width: ${(props) => `calc(100% - ${props.stepWidth}%)`};
      left: ${(props) => `${props.stepWidth / 2}%`};
      height: 2px;
      top: -2px;
    }
  }
`;

const TimelineStep = styled.li<{ width: number }>`
  position: relative;
  font-size: 12px;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 100px;

  @media (min-width: 568px) {
    flex-basis: ${(props) => `${props.width}%`};
  }
`;

const TimelineStepMarker = styled.i`
  width: 15px;
  height: 15px;
  margin-left: -7.5px;
  background: #fff;
  border: 2px solid #00457c;
  border-radius: 50%;
  -webkit-box-shadow: 0 0 0 0 #fff;
  box-shadow: 0 0 0 0 #fff;
  text-align: center;
  line-height: 15px -10;
  color: #00457c;
  font-size: 1em;
  font-style: normal;
  position: absolute;
  top: calc(50% - 8.5px);
  left: 50%;

  .done > & {
    background: #00457c;
  }

  @media (min-width: 568px) {
    top: -8.5px;
  }
`;

const TimelineStepProgress = styled.div<{ stepPercentage: number }>`
  background-color: #00457c;
  position: absolute;
  width: 2px;
  height: ${(props) => `${props.stepPercentage}%`};
  margin-left: calc(50% - 1px);
  top: 50px;

  @media (min-width: 568px) {
    width: ${(props) => `${props.stepPercentage}%`};
    height: 2px;
    margin-left: 50%;
    top: -2px;
  }
`;

const TimelineStepDate = styled.span`
  position: absolute;
  width: 50%;
  padding: 0 20px;
  left: 0;
  top: calc(50% - 9px);
  text-align: right;

  @media (min-width: 568px) {
    top: -25px;
    width: 100%;
    text-align: center;
  }
`;

const TimelineStepTitle = styled.span`
  display: block;
  text-align: left;
  position: absolute;
  width: 50%;
  padding: 0 20px;
  right: 0;
  top: calc(50% - 9px);

  @media (min-width: 568px) {
    padding: 10px 0 0;
    position: static;
    width: 100%;
    text-align: center;
  }
`;
